import React from 'react';
import type { OptionProps } from 'react-select';
import { components } from 'react-select';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { Field } from '../../components/Form';
import FormModal from '../../components/FormModal';
import { gql } from '../../graphql/generated/gql';
import { ExportFormat, ExportPrivacy } from '../../graphql/generated/graphql';
import { adminToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import * as styles from './MassExportModal.module.scss';

const EXPORT_ALL_MUTATION = gql(/** @complexity 2 */ `
  mutation exportAll($format: ExportFormat!, $privacy: ExportPrivacy!) {
    exportAll(format: $format, privacy: $privacy) {
      url
    }
  }
`);

type MassExportSelectOptionType = {
  label: string;
  value: ExportPrivacy;
  description: string;
};

interface MassExportSelectValueProps {
  data: MassExportSelectOptionType;
}

type MassExportSelectOptionProps = Omit<
  OptionProps<MassExportSelectOptionType, true>,
  'data'
> &
  MassExportSelectValueProps;

const MassExportSelectOption = (props: MassExportSelectOptionProps) => (
  <components.Option {...props}>
    <div className={styles.option}>
      <strong>{props.label}</strong>
      <span>{props.data.description}</span>
    </div>
  </components.Option>
);

interface MassExportModalProps {
  close: () => void;
}

type Values = {
  format: ExportFormat;
  privacy: ExportPrivacy;
};

const MassExportModal = ({ close }: MassExportModalProps) => {
  const dispatch = useAppDispatch();

  const [exportAll] = useMutation(EXPORT_ALL_MUTATION);

  const handleSubmit = async (values: Values) => {
    try {
      await exportAll({ variables: values });
      dispatch(UIActions.pushToast(adminToasts.massExportProcessed));
      close();
    } catch (err) {}
  };

  return (
    <FormModal<Values>
      title="Export published posts"
      close={close}
      description="Pick the type and scope to export"
      width={370}
      form={{
        initialValues: {
          format: ExportFormat.Markdown,
          privacy: ExportPrivacy.Open,
        },
        onSubmit: handleSubmit,
      }}
      footer={{
        submitButton: { text: 'Download' },
      }}
    >
      <Field
        label="File type"
        type="select"
        options={[
          { label: 'Markdown (.md)', value: ExportFormat.Markdown },
          { label: 'Word (.docx)', value: ExportFormat.Docx },
          // { label: 'PDF', value: 'pdf' },
        ]}
        name="format"
      />
      <Field
        label="Topics"
        type="select"
        options={[
          {
            label: 'Open and Public',
            description: 'Posts viewable by any standard user',
            value: ExportPrivacy.Open,
          },
          {
            label: 'Open, Public, and Private',
            description: 'Includes Private topics you are not a member of',
            value: ExportPrivacy.Private,
          },
          {
            label: 'All topics including Secret',
            description:
              'Does not include Secret topics that you do not have access to',
            value: ExportPrivacy.Secret,
          },
        ]}
        name="privacy"
        components={{ Option: MassExportSelectOption }}
      />
    </FormModal>
  );
};

export default MassExportModal;
